import { ApiError, sendNotification, useApiClient, useTranslation } from '@msdk/common'
import { stringFormat } from '@msdk/forms'
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'

export type CodeType = 'face' | 'nfc'

interface CodeInfo {
  code: OnboardingUserCodeIssuanceResponse | undefined
  isCodeExpired: boolean
  remainingTime: string
}

interface CodeContextType {
  face: CodeInfo
  nfc: CodeInfo
  generateCode: (type: CodeType) => Promise<void>
  invalidateCode: (type: CodeType) => void
}

const CodeContext = createContext<CodeContextType | undefined>(undefined)

export function CodeProvider({ children }: PropsWithChildren) {
  const t = useTranslation()
  const apiClient = useApiClient()

  const [faceCode, setFaceCode] = useState<OnboardingUserCodeIssuanceResponse | undefined>(undefined)
  const [faceIsCodeExpired, setFaceIsCodeExpired] = useState<boolean>(false)
  const [faceRemainingTime, setFaceRemainingTime] = useState<string>('')
  const faceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const faceCodeGeneratedRef = useRef<boolean>(false)

  const [nfcCode, setNfcCode] = useState<OnboardingUserCodeIssuanceResponse | undefined>(undefined)
  const [nfcIsCodeExpired, setNfcIsCodeExpired] = useState<boolean>(false)
  const [nfcRemainingTime, setNfcRemainingTime] = useState<string>('')
  const nfcTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const nfcCodeGeneratedRef = useRef<boolean>(false)

  const updateRemainingTime = useCallback(
    (type: CodeType) => {
      const isFace = type === 'face'
      const code = isFace ? faceCode : nfcCode
      if (!code?.expires_at) return

      const now = new Date()
      const expiry = new Date(code.expires_at)
      const diff = expiry.getTime() - now.getTime()

      if (diff <= 0) {
        if (isFace) {
          setFaceRemainingTime('00:00:00')
          setFaceIsCodeExpired(true)
          faceCodeGeneratedRef.current = false
          if (faceTimerRef.current) clearInterval(faceTimerRef.current)
        } else {
          setNfcRemainingTime('00:00:00')
          setNfcIsCodeExpired(true)
          nfcCodeGeneratedRef.current = false
          if (nfcTimerRef.current) clearInterval(nfcTimerRef.current)
        }
        return
      }

      const hours = Math.floor(diff / 3600000)
      const minutes = Math.floor((diff % 3600000) / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0',
      )}`

      if (isFace) {
        setFaceRemainingTime(timeStr)
      } else {
        setNfcRemainingTime(timeStr)
      }
    },
    [faceCode, nfcCode],
  )

  useEffect(() => {
    if (!faceCode?.expires_at) return
    updateRemainingTime('face')
    faceTimerRef.current = setInterval(() => updateRemainingTime('face'), 1000)
    return () => {
      if (faceTimerRef.current) clearInterval(faceTimerRef.current)
    }
  }, [faceCode?.expires_at, updateRemainingTime])

  useEffect(() => {
    if (!nfcCode?.expires_at) return
    updateRemainingTime('nfc')
    nfcTimerRef.current = setInterval(() => updateRemainingTime('nfc'), 1000)
    return () => {
      if (nfcTimerRef.current) clearInterval(nfcTimerRef.current)
    }
  }, [nfcCode?.expires_at, updateRemainingTime])

  const generateCode = useCallback(
    async (type: CodeType) => {
      const isFace = type === 'face'
      const codeRef = isFace ? faceCodeGeneratedRef : nfcCodeGeneratedRef
      const currentCode = isFace ? faceCode : nfcCode
      const isExpired = isFace ? faceIsCodeExpired : nfcIsCodeExpired

      if (codeRef.current && currentCode && !isExpired) return
      codeRef.current = true

      const body: OnboardingUserCodeIssuanceRequestDto = {
        session_type: type === 'face' ? 'biometric' : 'nfc',
      }
      const res = await apiClient.post<OnboardingUserCodeIssuanceResponse, OnboardingUserCodeIssuanceRequestDto>(
        'USM/api/v1/onboarding/usercode',
        body,
      )
      if (res instanceof ApiError) {
        console.error(`[CodeContext] - error generating ${type} user code`, res)
        sendNotification({
          message: stringFormat(t('LBL_NGID_ERROR_GENERATING_USER_CODE', 'Error generating {0} user code'), type),
          status: 'error',
        })
        return
      }

      if (isFace) {
        setFaceCode(res)
        setFaceIsCodeExpired(false)
      } else {
        setNfcCode(res)
        setNfcIsCodeExpired(false)
      }
    },
    [apiClient, t, faceCode, faceIsCodeExpired, nfcCode, nfcIsCodeExpired],
  )

  const invalidateCode = useCallback((type: CodeType) => {
    const isFace = type === 'face'
    if (isFace) {
      setFaceCode(undefined)
      setFaceIsCodeExpired(true)
      faceCodeGeneratedRef.current = false
      if (faceTimerRef.current) clearInterval(faceTimerRef.current)
      setFaceRemainingTime('')
    } else {
      setNfcCode(undefined)
      setNfcIsCodeExpired(true)
      nfcCodeGeneratedRef.current = false
      if (nfcTimerRef.current) clearInterval(nfcTimerRef.current)
      setNfcRemainingTime('')
    }
  }, [])

  return (
    <CodeContext.Provider
      value={{
        face: { code: faceCode, isCodeExpired: faceIsCodeExpired, remainingTime: faceRemainingTime },
        nfc: { code: nfcCode, isCodeExpired: nfcIsCodeExpired, remainingTime: nfcRemainingTime },
        generateCode,
        invalidateCode,
      }}
    >
      {children}
    </CodeContext.Provider>
  )
}

export function useCodeContext() {
  const context = useContext(CodeContext)
  if (!context) {
    throw new Error('useCodeContext must be used within a CodeProvider')
  }
  return context
}

interface OnboardingUserCodeIssuanceRequestDto {
  session_type: string
}

interface OnboardingUserCodeIssuanceResponse {
  user_code: string
  expires_at: string
}
